import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEye,
  faEdit,
  faTrash,
  faPlus,
  faSearch,
  faArrowLeft,
  faChartBar,
  faFileExcel,
  faSpinner,
  faCrosshairs,
  faInfoCircle,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getToken } from '../../function/token';
import domain from '../../../reusecomponent/domain';
import Swal from 'sweetalert2';
import Preloader from '../preloader';
import { ErrorToastContainer, SuccessToastContainer } from '../../function/CustomFunction';
import './managecampaigns.css';

// Sub-component: Campaign Status Badge
const StatusBadge = React.memo(({ status }) => {
  const statusMap = {
    'NOTSTARTED': { color: '#6c757d', bg: '#f8f9fa', label: 'Not Started' },
    'WAITING': { color: '#d97706', bg: '#fef3c7', label: 'Waiting' },
    'PROCESSING': { color: '#2563eb', bg: '#dbeafe', label: 'Processing' },
    'COMPLETED': { color: '#16a34a', bg: '#dcfce7', label: 'Completed' },
    'FAILED': { color: '#dc2626', bg: '#fee2e2', label: 'Failed' },
    'ERROR': { color: '#dc2626', bg: '#fee2e2', label: 'Error' }
  };

  const style = statusMap[status] || statusMap['NOTSTARTED'];

  return (
    <span
      className="badge px-3 py-2 rounded-pill fw-semibold text-uppercase"
      style={{ color: style.color, backgroundColor: style.bg, fontSize: '11px', letterSpacing: '0.5px' }}
    >
      {style.label}
    </span>
  );
});

// Sub-component: Recipient Delivery Status Badge
const DeliveryStatusBadge = React.memo(({ status, failedReason }) => {
  const [tooltipPos, setTooltipPos] = useState(null);

  const statusMap = {
    0: { class: 'bg-warning text-dark', label: 'Pending' },
    1: { class: 'bg-info text-white', label: 'Sent' },
    2: { class: 'bg-primary', label: 'Delivered' },
    3: { class: 'bg-success', label: 'Read' },
    4: { class: 'bg-danger', label: 'Failed' },
    5: { class: 'bg-dark', label: 'Deleted' },
    6: { class: 'bg-danger', label: 'Technical Error' }
  };

  const isFailed = Number(status) === 4 || Number(status) === 6;
  const item = statusMap[Number(status)] || { class: 'bg-secondary', label: 'Unknown' };

  const handleMouseEnter = (e) => {
    if (!failedReason || !isFailed) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPos({
      top: rect.bottom + 6,
      left: Math.max(10, rect.left)
    });
  };

  const handleMouseLeave = () => {
    setTooltipPos(null);
  };

  const handleClick = () => {
    if (failedReason && isFailed) {
      Swal.fire({
        title: 'Delivery Failed',
        html: `<div style="text-align:left; font-size:14px; word-break:break-word;"><strong>Error Details:</strong><br/><div class="mt-2 p-2" style="background:#fee2e2; color:#dc2626; border-radius:6px; font-family:monospace;">${failedReason}</div></div>`,
        icon: 'error',
        confirmButtonColor: '#075E54'
      });
    }
  };

  return (
    <div 
      className="d-inline-block"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      title={failedReason && isFailed ? "Click or hover for error details" : undefined}
      style={{ cursor: failedReason && isFailed ? 'pointer' : 'default' }}
    >
      <span className={`badge ${item.class} d-inline-flex align-items-center gap-1`}>
        {item.label}
        {failedReason && isFailed && (
          <FontAwesomeIcon icon={faInfoCircle} style={{ fontSize: '10px' }} />
        )}
      </span>

      {/* Fixed Position Tooltip Popover (Guarantees zero clipping by table container) */}
      {tooltipPos && failedReason && isFailed && (
        <div
          className="shadow-lg rounded-3 p-3 text-start position-fixed"
          style={{
            top: `${tooltipPos.top}px`,
            left: `${tooltipPos.left}px`,
            zIndex: 99999,
            width: '280px',
            backgroundColor: '#1e293b',
            color: '#f8fafc',
            fontSize: '12px',
            lineHeight: '1.4',
            pointerEvents: 'none',
            border: '1px solid #334155',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.5)'
          }}
        >
          <div className="fw-semibold text-warning mb-1 d-flex align-items-center gap-1">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-warning me-1" />
            Failure Reason Details
          </div>
          <div style={{ wordBreak: 'break-word', whiteSpace: 'normal', color: '#cbd5e1' }}>
            {failedReason}
          </div>
          <div className="mt-2 pt-1 border-top border-secondary text-muted" style={{ fontSize: '10px' }}>
            Click badge to view full alert
          </div>
        </div>
      )}
    </div>
  );
});

// Sub-component: Table Row
const CampaignTableRow = React.memo(({ campaign, handleView, downloadExcel, handleEdit, handleDelete, handleViewAnalytics, handleRetarget, loadingCampaignId, isRetargetingEnabled }) => {
  const isLoading = loadingCampaignId === campaign.id;

  const formattedCreatedDate = useMemo(() => {
    try {
      return new Date(campaign.createdAt).toLocaleString('en-GB').replace(',', '').replace(/\//g, '.');
    } catch {
      return '-';
    }
  }, [campaign.createdAt]);

  const formattedScheduled = useMemo(() => {
    if (!campaign.campaign_date && !campaign.campaign_time) return 'Not Scheduled';
    const dateStr = campaign.campaign_date ? new Date(campaign.campaign_date).toLocaleDateString('en-GB').replace(/\//g, '.') : '';
    const timeStr = campaign.campaign_time ? campaign.campaign_time : '';
    return `${dateStr} ${timeStr}`.trim();
  }, [campaign.campaign_date, campaign.campaign_time]);

  return (
    <tr>
      <td className="fw-bold text-dark">{campaign.name}</td>
      <td>
        <span className="badge bg-light text-primary border">{campaign.campaign_type || 'Broadcast'}</span>
      </td>
      <td className="text-center">
        <StatusBadge status={campaign.que} />
      </td>
      <td className="text-center fw-semibold">{campaign.sent.toLocaleString()}</td>
      <td className="text-center fw-semibold">{campaign.delivered.toLocaleString()}</td>
      <td className="text-center fw-semibold">{campaign.opened.toLocaleString()}</td>
      <td className="text-center fw-semibold text-danger">{campaign.failed.toLocaleString()}</td>
      <td className="text-center fw-semibold">{campaign.deduct_amount !== undefined && campaign.deduct_amount !== null ? campaign.deduct_amount : 0}</td>
      <td className="small text-muted">{formattedCreatedDate}</td>
      <td className="small text-muted">{formattedScheduled}</td>
      <td>
        <span className="badge bg-success text-white">{campaign.broadcastBy || 'System'}</span>
      </td>
      <td className="text-center">
        <div className="btn-group gap-1" role="group">
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => handleView(campaign.id)}
            title="View Details"
            disabled={isLoading}
          >
            <FontAwesomeIcon icon={faEye} />
          </button>
          <button
            className="btn btn-sm btn-outline-success"
            onClick={() => downloadExcel(campaign.id, campaign.name, formattedCreatedDate)}
            title="Download Excel Report"
            disabled={isLoading}
          >
            <FontAwesomeIcon icon={faFileExcel} />
          </button>
          <button
            className="btn btn-sm btn-outline-secondary"
            style={{
              color: isRetargetingEnabled ? '#075E54' : '#94a3b8',
              borderColor: isRetargetingEnabled ? '#075E54' : '#cbd5e1',
              opacity: isRetargetingEnabled ? 1 : 0.6
            }}
            onClick={() => handleRetarget(campaign.id)}
            title={isRetargetingEnabled ? "Retarget Audience" : "Retargeting feature not enabled"}
            disabled={isLoading}
          >
            <FontAwesomeIcon icon={faCrosshairs} />
          </button>
          <button
            className="btn btn-sm btn-outline-warning"
            onClick={() => handleEdit(campaign.id)}
            title="Edit Campaign"
            disabled={isLoading}
          >
            <FontAwesomeIcon icon={faEdit} />
          </button>
          <button
            className="btn btn-sm btn-outline-danger"
            onClick={() => handleDelete(campaign.id)}
            title="Delete Campaign"
            disabled={isLoading}
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
          <button
            className="btn btn-sm btn-outline-info"
            onClick={() => handleViewAnalytics(campaign)}
            title="View Analytics"
            disabled={isLoading}
          >
            <FontAwesomeIcon icon={faChartBar} />
          </button>
        </div>
      </td>
    </tr>
  );
});

function ManageCampaigns() {
  const navigate = useNavigate();
  const messageRetargetingService = useSelector((state) => state?.serviceStore?.value?.message_retargeting);
  const isRetargetingEnabled = Boolean(
    messageRetargetingService && 
    messageRetargetingService !== "0" && 
    messageRetargetingService !== 0
  );

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Campaign details state
  const [showCampaignDetails, setShowCampaignDetails] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignMessages, setCampaignMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [loadingCampaignId, setLoadingCampaignId] = useState(null);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [editFormData, setEditFormData] = useState({ name: '', date: '', time: '' });
  const [editLoading, setEditLoading] = useState(false);

  // Analytics modal state
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [selectedCampaignForAnalytics, setSelectedCampaignForAnalytics] = useState(null);

  // Fetch campaigns from API
  const fetchCampaigns = useCallback(async () => {
    const token = getToken();
    if (!token) return;

    try {
      setLoading(true);
      const response = await fetch(`${domain()}/campaign`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.status && data.data?.data) {
        const transformed = data.data.data.map(campaign => ({
          id: campaign.id,
          name: campaign.campaign_name,
          type: 'Broadcast Campaign',
          recipients: campaign.message_pending + campaign.message_sent + campaign.message_delivered + campaign.message_read + campaign.message_failed + campaign.message_deleted,
          sent: campaign.message_sent || 0,
          campaign_type: campaign.campaign_type || 'Broadcast',
          delivered: campaign.message_delivered || 0,
          opened: campaign.message_read || 0,
          failed: campaign.message_failed || 0,
          deleted: campaign.message_deleted || 0,
          pending: campaign.message_pending || 0,
          deduct_amount: campaign.deduct_amount !== undefined && campaign.deduct_amount !== null ? campaign.deduct_amount : 0,
          createdAt: campaign.created_at,
          updatedAt: campaign.updated_at,
          broadcastBy: campaign.broadcast_by,
          templateId: campaign.template_id,
          que: campaign.que,
          campaign_date: campaign.campaign_date,
          campaign_time: campaign.campaign_time
        }));
        setCampaigns(transformed);
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      ErrorToastContainer('Error loading campaigns');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  // Search filtering & pagination
  const filteredCampaigns = useMemo(() => {
    return campaigns.filter(campaign =>
      campaign.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      campaign.campaign_type.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [campaigns, searchTerm]);

  const totalPages = Math.ceil(filteredCampaigns.length / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedCampaigns = useMemo(() => {
    return filteredCampaigns.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredCampaigns, startIndex, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(p => p - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(p => p + 1);
  };

  // Retarget Action Handler - Base64 encodes campaign ID in query parameter
  const handleRetarget = useCallback((campaignId) => {
    if (!isRetargetingEnabled) {
      ErrorToastContainer("Your message retargeting feature is not enabled");
      return;
    }
    const encodedId = btoa(String(campaignId));
    navigate(`/client-area/whatsapp/campaign/retargeting?sourceId=${encodedId}`);
  }, [navigate, isRetargetingEnabled]);

  // Delete Campaign
  const handleDelete = useCallback(async (campaignId) => {
    const result = await Swal.fire({
      title: 'Delete Campaign?',
      text: "This action will permanently delete the campaign and its reports.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      setLoading(true);
      try {
        const token = getToken();
        const response = await fetch(`${domain()}/campaign/${btoa(campaignId)}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        const data = await response.json();

        if (data.status) {
          setCampaigns(prev => prev.filter(c => c.id !== campaignId));
          SuccessToastContainer('Campaign deleted successfully');
        } else {
          ErrorToastContainer(data.message || 'Failed to delete campaign');
        }
      } catch (error) {
        ErrorToastContainer('Error deleting campaign');
      } finally {
        setLoading(false);
      }
    }
  }, []);

  // Edit Campaign Modal Handlers
  const handleEdit = useCallback((campaignId) => {
    const campaign = campaigns.find(c => c.id === campaignId);
    if (!campaign) return;

    const formattedDate = campaign.campaign_date ? new Date(campaign.campaign_date).toISOString().split('T')[0] : '';
    setEditFormData({
      name: campaign.name || '',
      date: formattedDate,
      time: campaign.campaign_time || ''
    });
    setEditingCampaign(campaign);
    setShowEditModal(true);
  }, [campaigns]);

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editFormData.name.trim() || !editFormData.date || !editFormData.time) {
      ErrorToastContainer('Please fill in all required fields');
      return;
    }

    setEditLoading(true);
    setLoading(true);
    try {
      const token = getToken();
      const response = await fetch(`${domain()}/campaign/${btoa(editingCampaign.id)}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          campaign_name: editFormData.name,
          campaign_date: editFormData.date,
          campaign_time: editFormData.time
        })
      });

      const data = await response.json();
      if (data.status) {
        setCampaigns(prev => prev.map(c =>
          c.id === editingCampaign.id
            ? { ...c, name: editFormData.name, campaign_date: editFormData.date, campaign_time: editFormData.time }
            : c
        ));
        SuccessToastContainer('Campaign updated successfully');
        setShowEditModal(false);
      } else {
        ErrorToastContainer(data.message || 'Failed to update campaign');
      }
    } catch (error) {
      ErrorToastContainer('Error updating campaign');
    } finally {
      setEditLoading(false);
      setLoading(false);
    }
  };

  // View Details
  const handleView = useCallback(async (campaignId) => {
    setLoading(true);
    setMessagesLoading(true);
    setLoadingCampaignId(campaignId);
    try {
      const campaign = campaigns.find(c => c.id === campaignId);
      setSelectedCampaign(campaign);

      const token = getToken();
      const response = await fetch(`${domain()}/campaign_list/${btoa(campaignId)}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.status) {
        setCampaignMessages(data.data?.data || []);
        setShowCampaignDetails(true);
      } else {
        ErrorToastContainer(data.message || 'Failed to load details');
      }
    } catch (error) {
      ErrorToastContainer('Error fetching details');
    } finally {
      setMessagesLoading(false);
      setLoadingCampaignId(null);
      setLoading(false);
    }
  }, [campaigns]);

  // Download Excel Report
  const downloadExcel = useCallback(async (campaignId, name, createdDate) => {
    setLoading(true);
    setLoadingCampaignId(campaignId);
    try {
      const token = getToken();
      const response = await fetch(`${domain()}/campaign/downlode_excel/${btoa(campaignId)}`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${name}_${createdDate || 'report'}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      ErrorToastContainer('Error downloading report');
    } finally {
      setLoadingCampaignId(null);
      setLoading(false);
    }
  }, []);

  // View Analytics
  const handleViewAnalytics = useCallback((campaign) => {
    setSelectedCampaignForAnalytics(campaign);
    setShowAnalyticsModal(true);
  }, []);

  return (
    <div className="main-panel w-100" style={{ minHeight: '85vh' }}>
      <Preloader action={loading} />
      <div className="content-wrapper">
        <div className="manage-campaigns-header">
          <div className="header-content">
            <h4>Manage WhatsApp Campaigns</h4>
            <p>Monitor campaign delivery, schedule broadcasts, and view analytics.</p>
          </div>
          <button
            className="btn btn-primary create-campaign-btn px-4 py-2 rounded-pill fw-semibold d-inline-flex align-items-center gap-2 shadow-sm"
            style={{ backgroundColor: '#075E54', borderColor: '#075E54', color: '#ffffff', border: 'none' }}
            onClick={() => navigate('/client-area/whatsapp/broadcast-campaign')}
          >
            <FontAwesomeIcon icon={faPlus} />
            <span>Create New Campaign</span>
          </button>
        </div>

        {/* Campaign Details Full Screen View */}
        {showCampaignDetails ? (
          <div className="card border-0 shadow-sm" style={{ borderRadius: '20px', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
            <div className="card-header bg-white p-4 border-bottom d-flex justify-content-between align-items-center">
              <div>
                <h4 className="fw-bold text-dark mb-1">Campaign Details: {selectedCampaign?.name}</h4>
                <p className="text-muted mb-0 small">Delivery metrics and recipient logs</p>
              </div>
              <button
                className="btn btn-outline-secondary px-4 rounded-pill"
                onClick={() => setShowCampaignDetails(false)}
              >
                <FontAwesomeIcon icon={faArrowLeft} className="me-2" /> Back to Campaigns
              </button>
            </div>

            <div className="card-body p-4">
              {messagesLoading ? (
                <div className="text-center py-5">
                  <FontAwesomeIcon icon={faSpinner} spin size="2x" className="text-success mb-2" />
                  <p className="text-muted">Loading recipient details...</p>
                </div>
              ) : (
                <div className="table-responsive" style={{ minHeight: '180px', borderRadius: '12px' }}>
                  <table className="table payment-history-table">
                    <thead>
                      <tr>
                        <th>Recipient</th>
                        <th>Phone Number</th>
                        <th>Delivery Status</th>
                        <th>Date & Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {campaignMessages.map((msg) => (
                        <tr key={msg.id || Math.random()}>
                          <td className="fw-medium">{msg.name || 'N/A'}</td>
                          <td className="font-monospace">{msg.phone_number}</td>
                          <td>
                            <DeliveryStatusBadge status={msg.message_deliver} failedReason={msg.failed_reason} />
                          </td>
                          <td className="small text-muted">{new Date(msg.created_at).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="card border-0 shadow-sm p-4" style={{ borderRadius: '20px', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
            {/* Search bar */}
            <div className="row mb-4">
              <div className="col-md-6">
                <div className="input-group">
                  <span className="input-group-text bg-white border-end-0">
                    <FontAwesomeIcon icon={faSearch} className="text-muted" />
                  </span>
                  <input
                    type="text"
                    className="form-control border-start-0"
                    placeholder="Search campaigns..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Table View */}
            <div className="table-responsive" style={{ borderRadius: '12px' }}>
              <table className="table payment-history-table">
                <thead>
                  <tr>
                    <th>Campaign Name</th>
                    <th>Type</th>
                    <th className="text-center">Status</th>
                    <th className="text-center">Sent</th>
                    <th className="text-center">Delivered</th>
                    <th className="text-center">Opened</th>
                    <th className="text-center">Failed</th>
                    <th className="text-center">Charges</th>
                    <th>Created</th>
                    <th>Schedule</th>
                    <th>By</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedCampaigns.length === 0 ? (
                    <tr>
                      <td colSpan="12" className="text-center py-5 text-muted">
                        No campaigns found. Click "Create New Campaign" to get started.
                      </td>
                    </tr>
                  ) : (
                    paginatedCampaigns.map(c => (
                      <CampaignTableRow
                        key={c.id}
                        campaign={c}
                        handleView={handleView}
                        downloadExcel={downloadExcel}
                        handleEdit={handleEdit}
                        handleDelete={handleDelete}
                        handleViewAnalytics={handleViewAnalytics}
                        handleRetarget={handleRetarget}
                        loadingCampaignId={loadingCampaignId}
                        isRetargetingEnabled={isRetargetingEnabled}
                      />
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="d-flex justify-content-between align-items-center mt-4">
              <small className="text-muted">
                Showing {paginatedCampaigns.length} of {filteredCampaigns.length} campaigns
              </small>
              <div className="btn-group">
                <button className="btn btn-outline-secondary btn-sm" onClick={handlePreviousPage} disabled={currentPage === 1}>
                  Previous
                </button>
                <button className="btn btn-outline-secondary btn-sm" onClick={handleNextPage} disabled={currentPage === totalPages}>
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content rounded-4 border-0">
              <div className="modal-header bg-success text-white p-4">
                <h5 className="modal-title fw-bold">Edit Campaign</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowEditModal(false)}></button>
              </div>
              <form onSubmit={handleEditSubmit}>
                <div className="modal-body p-4">
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Campaign Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editFormData.name}
                      onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="row g-3">
                    <div className="col-6">
                      <label className="form-label fw-semibold">Schedule Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={editFormData.date}
                        onChange={(e) => setEditFormData({ ...editFormData, date: e.target.value })}
                        required
                      />
                    </div>
                    <div className="col-6">
                      <label className="form-label fw-semibold">Schedule Time</label>
                      <input
                        type="time"
                        className="form-control"
                        value={editFormData.time}
                        onChange={(e) => setEditFormData({ ...editFormData, time: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                </div>
                <div className="modal-footer p-3 bg-light">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-success px-4" disabled={editLoading}>
                    {editLoading ? 'Saving...' : 'Update Campaign'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Analytics Modal */}
      {showAnalyticsModal && selectedCampaignForAnalytics && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content rounded-4 border-0">
              <div className="modal-header bg-dark text-white p-4">
                <h5 className="modal-title fw-bold">Analytics: {selectedCampaignForAnalytics.name}</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowAnalyticsModal(false)}></button>
              </div>
              <div className="modal-body p-4">
                <div className="row g-3 text-center mb-4">
                  <div className="col-3">
                    <div className="p-3 bg-success bg-opacity-10 border border-success rounded-3">
                      <h3 className="text-success fw-bold m-0">{selectedCampaignForAnalytics.sent}</h3>
                      <small className="text-muted">Sent</small>
                    </div>
                  </div>
                  <div className="col-3">
                    <div className="p-3 bg-info bg-opacity-10 border border-info rounded-3">
                      <h3 className="text-info fw-bold m-0">{selectedCampaignForAnalytics.delivered}</h3>
                      <small className="text-muted">Delivered</small>
                    </div>
                  </div>
                  <div className="col-3">
                    <div className="p-3 bg-warning bg-opacity-10 border border-warning rounded-3">
                      <h3 className="text-warning fw-bold m-0">{selectedCampaignForAnalytics.opened}</h3>
                      <small className="text-muted">Read</small>
                    </div>
                  </div>
                  <div className="col-3">
                    <div className="p-3 bg-danger bg-opacity-10 border border-danger rounded-3">
                      <h3 className="text-danger fw-bold m-0">{selectedCampaignForAnalytics.failed}</h3>
                      <small className="text-muted">Failed</small>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer bg-light p-3">
                <button className="btn btn-secondary px-4" onClick={() => setShowAnalyticsModal(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageCampaigns;